import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeDonutSlices } from '../../components/display/donut-chart.js';
import { DonutChart } from '../../components/display/donut-chart.jsx';

/**
 * One "chart + table" block of the Wealth Breakdown report: a donut showing
 * the allocation next to the table (below it on narrow screens), with a
 * swatch column and subtotal row. Hovering a slice or row highlights its match.
 * @param {Object} props
 * @param {string} props.title section heading
 * @param {string} props.headName label for the first table column
 * @param {string} props.ariaLabel accessible name for the donut
 * @param {Record<string, number>} props.values key -> value in the base currency
 * @param {(key: string) => string} props.labelOf row label for a key
 * @param {(key: string | null, value: number) => import('react').ReactNode} props.amountOf
 *        formatted amount for a key (null key = the subtotal / centre total)
 * @param {number} props.total subtotal shown in the table footer
 * @param {string} [props.headName2] header for an optional second amount column
 * @param {(key: string | null, value: number) => import('react').ReactNode} [props.amountOf2]
 *        formatted amount for the second column (renders it when provided)
 * @param {(value: number) => import('react').ReactNode} [props.subtotalOf]
 *        overrides the first amount column's subtotal cell (defaults to amountOf(null, total))
 * @returns {JSX.Element}
 */
export function WealthSection({
	title,
	headName,
	ariaLabel,
	values,
	labelOf,
	amountOf,
	total,
	headName2,
	amountOf2,
	subtotalOf,
}) {
	const { t } = useTranslation();
	const [activeKey, setActiveKey] = useState(null);
	const { entries } = computeDonutSlices(values);
	const swatchOf = new Map(entries.map((entry) => [entry.key, `swatch-${entry.colorIndex}`]));
	const tableClass = `report-table wealth-table${amountOf2 ? ' wealth-table--currency' : ' wealth-table--type'}${activeKey ? ' is-hovering' : ''}`;

	const rows = Object.entries(values)
		.sort((a, b) => b[1] - a[1])
		.map(([key, value]) => ({
			key,
			value,
			swatchClass: swatchOf.has(key) ? swatchOf.get(key) : 'swatch-muted',
			pct: total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '\u2014',
		}));

	const handleRowOver = (event) => {
		const row = /** @type {HTMLElement | null} */ (event.target).closest('tr[data-key]');
		if (row) { setActiveKey(row.dataset.key ?? null); }
	};

	return (
		<div className="report-section">
			<h3>{title}</h3>
			<div className="wealth-grid">
				<DonutChart
					values={values}
					amount={amountOf(null, total)}
					label={t('report.total')}
					ariaLabel={ariaLabel}
					activeKey={activeKey}
					onHover={setActiveKey}
				/>
				<table className={tableClass}>
					<thead>
						<tr>
							<th className="swatch-head"></th>
							<th className="wealth-col-label">{headName}</th>
							<th className="right wealth-col-value">{t('report.colNetValue')}</th>
							{amountOf2 && <th className="right wealth-col-value col-priority-2">{headName2}</th>}
							<th className="right wealth-col-pct">{t('report.colPercent')}</th>
						</tr>
					</thead>
					<tbody onMouseOver={handleRowOver} onMouseLeave={() => setActiveKey(null)}>
						{rows.map((row) => (
							<tr
								key={row.key}
								data-key={row.key}
								className={activeKey === row.key ? 'is-active' : undefined}
							>
								<td className="swatch-cell"><span className={`swatch ${row.swatchClass}`} /></td>
								<td className="wealth-col-label">{labelOf(row.key)}</td>
								<td className="right wealth-col-value">{amountOf(row.key, row.value)}</td>
								{amountOf2 && <td className="right wealth-col-value col-priority-2">{amountOf2(row.key, row.value)}</td>}
								<td className="right wealth-col-pct">{row.pct}</td>
							</tr>
						))}
						<tr className="report-subtotal">
							<td></td>
							<td className="wealth-col-label">{t('report.totalNetWorth')}</td>
							<td className="right wealth-col-value">{subtotalOf ? subtotalOf(total) : amountOf(null, total)}</td>
							{amountOf2 && <td className="right wealth-col-value col-priority-2">{amountOf2(null, total)}</td>}
							<td className="right wealth-col-pct">{total !== 0 ? '100.0%' : '\u2014'}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}
